/* Display formatting shared by both surfaces.
 *
 * These live in shared because a coordinate read off the desk screen and one
 * read off a phone have to be character-for-character identical — someone is
 * saying them out loud over a radio to someone looking at the other one.
 */

export const formatCoord = (v: number, axis: 'lat' | 'lon'): string => {
  const hemi = axis === 'lat' ? (v >= 0 ? 'N' : 'S') : v >= 0 ? 'E' : 'W';
  return `${Math.abs(v).toFixed(5)}° ${hemi}`;
};

/** Accuracy is part of the fact, so it is never rounded away to nothing. */
export const formatAccuracy = (m: number): string =>
  m < 0 ? 'not recorded' : `±${m < 10 ? m.toFixed(1) : Math.round(m)} m`;

export const metersToFeet = (m: number): number => m * 3.28084;

export const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

export const formatDuration = (seconds: number): string => {
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const clockTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

export const relativeAge = (iso: string): string => {
  const hours = (Date.now() - Date.parse(iso)) / 3_600_000;
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/**
 * Date and time together, for the desk and for settings — anywhere the answer
 * to "when" needs a day attached because it may not be today.
 *
 * Four call sites were reaching for `new Date(x).toLocaleString()` directly,
 * which renders differently per locale and per surface. The point of putting
 * it here is the same as `formatCoord`: two people reading the same record on
 * two devices should see the same characters.
 */
export const dateTime = (iso: string): string =>
  new Date(iso).toLocaleString([], {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
